import type { ReportDetail } from '../api/DigiobcanApi';
import DigiobcanApi from '../api/DigiobcanApi';
import { shortId } from '../lib/ids';
import { QueryNameSpace, useM } from '../lib/query';
import { __, formatDateTime, toastError } from '../utils';
import ConfirmationDialog from './ConfirmationDialog';
import { Button, Typography } from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Form } from 'react-final-form';
import { Link } from 'react-router';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  voteToken: {
    token_id: string;
    target: string;
    inserted_at?: string;
    expires_at?: string;
    revoked_at?: string;
  };
};

type VotingValues = {
  count: number;
};

const VotingTokenTransactionDetail: React.FC<Props> = ({
  open,
  onClose,
  voteToken,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  const [targetName, setTargetName] = React.useState<string>('');
  const [expireDateName, setExpireDateName] = React.useState<string>('');

  React.useEffect(() => {
    if (open) {
      setTargetName(voteToken.target);
      setExpireDateName(voteToken.expires_at);
    }
  }, [open, voteToken]);

  const meVoteTokenRevokeMutation = useM(
    (values: VotingValues) => {
      return DigiobcanApi.meVoteTokenRevoke(undefined, {
        count: values.count,
        target: voteToken.target,
      });
    },
    {
      onSuccess: async () => {
        onSuccess?.();
      },
      onError: () => {
        toastError(__('Nepodarilo sa zrušiť hlasovanie'));
      },
    },
  );
  /**
   * Odvolanie hlasu z profilu sa musí prepísať do tých istých cache, aké
   * aktualizuje hlasovanie na detaile podnetu (`VoteCardActions`) - inak by
   * detail podnetu po návrate ukazoval hlas, ktorý už neexistuje. Tvary tu
   * musia sedieť s tvarmi tam.
   */
  const updateQueries = async (reportId: string, revokedCount: number) => {
    // Fond žetónov občana: odvolané hlasy sa vracajú do `available`.
    queryClient.setQueryData(
      [QueryNameSpace.meGetVoteTokensSummary],
      (oldData?: {
        active_tokens: number;
        available_tokens: number;
        max_count: number;
      }) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          active_tokens: (oldData.active_tokens || 0) - revokedCount,
          available_tokens: (oldData.available_tokens || 0) + revokedCount,
        };
      },
    );

    // Ako občan stojí na tomto podnete.
    queryClient.setQueryData(
      [QueryNameSpace.meGetTargetVoteTokens, { target: reportId }],
      (oldData?: { active_tokens: number; available_tokens: number }) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          active_tokens: (oldData.active_tokens || 0) - revokedCount,
          available_tokens: (oldData.available_tokens || 0) + revokedCount,
        };
      },
    );

    // Počet hlasov na podnete.
    queryClient.setQueryData(
      [QueryNameSpace.getReportDetail, { reportId }],
      (oldData: ReportDetail) => {
        if (!oldData || typeof oldData !== 'object') return oldData;
        return {
          ...oldData,
          votes_count: (oldData.votes_count || 0) - revokedCount,
        };
      },
    );

    // Aktualizujeme transakcie hlasovani v profile - bez ohľadu na to, s akými
    // parametrami je zoznam žetónov práve načítaný.
    await queryClient.refetchQueries({
      queryKey: [QueryNameSpace.meGetVoteTokens],
    });
  };

  const formId = 'report-voting-form';

  return (
    <Form
      onSubmit={async (values: VotingValues) => {
        const { revoked_count } =
          await meVoteTokenRevokeMutation.mutateAsync(values);
        await updateQueries(voteToken.target, revoked_count ?? values.count);

        onClose();
      }}
      initialValues={{ count: 1 }}
      render={({ handleSubmit }) => {
        return (
          <ConfirmationDialog
            open={open}
            onClose={onClose}
            title={__('Detail hlasovacieho žetónu')}
            formId={formId}
            confirmLabel={__('Odvolať')}
            fullWidth
            maxWidth='xs'
            buttons={
              <Button
                color='info'
                component={Link}
                // bug in @presov.sk/presov-ui-react Button type
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                to={'/podnety/' + shortId(targetName)}
                onClick={onClose}
                style={{ textDecoration: 'none' }}
              >
                {__('Zobraziť podnet')}
              </Button>
            }
          >
            <form id={formId} onSubmit={handleSubmit}>
              <Typography display={'inline-block'}>
                {__('Hlasovali ste za podnet')}
              </Typography>{' '}
              <Typography
                display={'inline-block'}
                component='span'
                fontWeight={500}
              >
                {targetName}
              </Typography>
              <Typography display={'inline-block'}>{__('.')}</Typography>
              {expireDateName && (
                <Typography display={'inline-block'}>
                  {__(
                    ` Tento žetón ${new Date() > new Date(expireDateName) ? 'expiroval' : 'expiruje'}: ${formatDateTime(new Date(expireDateName))}.`,
                  )}
                </Typography>
              )}
            </form>
          </ConfirmationDialog>
        );
      }}
    />
  );
};

export default VotingTokenTransactionDetail;
